package fr.frux.core.maps

import fr.frux.core.common.logging.Logger
import kotlinx.coroutines.channels.awaitClose
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.callbackFlow
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlinx.coroutines.withTimeoutOrNull
import kotlin.coroutines.resume
import kotlin.js.json

private const val TAG = "LocationProvider"

// Meme budget que les autres plateformes (fix GPS a froid = quelques secondes).
private const val FIX_TIMEOUT_MS = 6_000L

// Position du fix ponctuel : assez recente pour ne pas afficher le point la ou la personne etait
// avant de marcher, sans forcer un nouveau fix a chaque tap.
private const val MAX_CACHED_AGE_MS = 10_000

// Chemin « derniere position connue » : on accepte une position agee (10 min) pourvu qu'elle
// soit rendue tout de suite — c'est l'equivalent web du lastLocation d'Android.
private const val CACHED_LOOKUP_TIMEOUT_MS = 500L
private const val CACHED_MAX_AGE_MS = 600_000

// Codes de GeolocationPositionError (https://w3c.github.io/geolocation-api/#position_error_interface).
internal const val ERROR_PERMISSION_DENIED = 1

/**
 * Geolocation API du navigateur, calibree Chrome comme le reste de la cible web (ADR 0007).
 *
 * Le contrat interdit au provider de declencher la popup de permission : `getCurrentPosition`
 * n'est appele que si la Permissions API repond `granted`, ou si un accord a deja ete observe
 * dans la session (cf. [geolocationAccess], iOS ne rapporte jamais `granted`). Les etats
 * `prompt` et `denied` rendent PermissionMissing — c'est le LocationPermissionController qui
 * gere la demande.
 */
private object JsLocationProvider : LocationProvider {
    override suspend fun currentLocation(): LocationResult {
        val state = queryGeolocationPermissionState()
        when (currentGeolocationAccess(state)) {
            GeolocationAccess.ALLOWED -> {
                Unit
            }

            GeolocationAccess.MISSING -> {
                return LocationResult.PermissionMissing
            }

            GeolocationAccess.UNAVAILABLE -> {
                Logger.i(TAG, "Permissions API indisponible (etat=$state)")
                return LocationResult.Unavailable
            }
        }

        val location = withTimeoutOrNull(FIX_TIMEOUT_MS + 500) { requestPosition() }
        return if (location == null) {
            Logger.i(TAG, "Aucune position obtenue (timeout ou echec Geolocation API)")
            LocationResult.Unavailable
        } else {
            LocationResult.Available(location)
        }
    }

    override suspend fun hasPermission(): Boolean =
        currentGeolocationAccess(queryGeolocationPermissionState()) == GeolocationAccess.ALLOWED

    override suspend fun lastKnownLocation(): Location? {
        if (!hasPermission()) return null
        // maximumAge long + timeout court : la Geolocation API rend une position deja en cache
        // ou echoue tout de suite — jamais de fix frais sur ce chemin.
        return withTimeoutOrNull(CACHED_LOOKUP_TIMEOUT_MS + 500) {
            requestPosition(
                timeoutMs = CACHED_LOOKUP_TIMEOUT_MS,
                maximumAgeMs = CACHED_MAX_AGE_MS,
                highAccuracy = false,
            )
        }
    }

    override fun locationUpdates(): Flow<Location> =
        callbackFlow {
            // Meme regle que currentLocation : sans accord observe, watchPosition ouvrirait la popup.
            val geolocation = browserGeolocation()
            val allowed = currentGeolocationAccess(queryGeolocationPermissionState()) == GeolocationAccess.ALLOWED
            if (geolocation == null || !allowed) {
                close()
                return@callbackFlow
            }
            val watchId =
                geolocation.watchPosition(
                    { position -> trySend(Location(position.coords.latitude, position.coords.longitude)) },
                    { error ->
                        Logger.i(TAG, "watchPosition a echoue (code=${error.code}): ${error.message}")
                        // Un refus en cours de route termine le suivi, les autres erreurs (timeout,
                        // position indisponible) sont transitoires et le navigateur reessaie.
                        if (error.code == ERROR_PERMISSION_DENIED) close()
                    },
                    json("enableHighAccuracy" to true, "maximumAge" to 0),
                )
            awaitClose { geolocation.clearWatch(watchId) }
        }

    private suspend fun requestPosition(
        timeoutMs: Long = FIX_TIMEOUT_MS,
        maximumAgeMs: Int = MAX_CACHED_AGE_MS,
        highAccuracy: Boolean = true,
    ): Location? =
        suspendCancellableCoroutine { continuation ->
            val geolocation = browserGeolocation()
            if (geolocation == null) {
                continuation.resume(null)
                return@suspendCancellableCoroutine
            }
            geolocation.getCurrentPosition(
                { position ->
                    val coords = position.coords
                    if (continuation.isActive) {
                        continuation.resume(Location(coords.latitude, coords.longitude))
                    }
                },
                { error ->
                    Logger.i(TAG, "getCurrentPosition a echoue (code=${error.code}): ${error.message}")
                    if (continuation.isActive) {
                        continuation.resume(null)
                    }
                },
                // Sans enableHighAccuracy, Chrome Android sert une position reseau, parfois a
                // plusieurs centaines de metres.
                json("timeout" to timeoutMs, "maximumAge" to maximumAgeMs, "enableHighAccuracy" to highAccuracy),
            )
        }
}

actual fun createLocationProvider(): LocationProvider = JsLocationProvider

// Le stdlib js ne declare pas la Geolocation API : externals minimales, acces via dynamic.

internal external interface JsGeolocation {
    fun getCurrentPosition(
        success: (JsGeolocationPosition) -> Unit,
        error: (JsGeolocationPositionError) -> Unit,
        options: dynamic,
    )

    fun watchPosition(
        success: (JsGeolocationPosition) -> Unit,
        error: (JsGeolocationPositionError) -> Unit,
        options: dynamic,
    ): Int

    fun clearWatch(id: Int)
}

internal external interface JsGeolocationPosition {
    val coords: JsGeolocationCoordinates
}

internal external interface JsGeolocationCoordinates {
    val latitude: Double
    val longitude: Double
}

internal external interface JsGeolocationPositionError {
    val code: Int
    val message: String
}

internal fun browserGeolocation(): JsGeolocation? {
    val geolocation: dynamic = js("navigator.geolocation ? navigator.geolocation : null")
    return geolocation.unsafeCast<JsGeolocation?>()
}

/**
 * Etat de la permission geolocalisation : "granted", "prompt", "denied", ou null si la
 * Permissions API est absente ou en echec.
 */
internal suspend fun queryGeolocationPermissionState(): String? =
    suspendCancellableCoroutine { continuation ->
        val permissions: dynamic = js("navigator.permissions ? navigator.permissions : null")
        if (permissions == null) {
            continuation.resume(null)
            return@suspendCancellableCoroutine
        }
        permissions.query(json("name" to "geolocation")).then(
            { status: dynamic ->
                if (continuation.isActive) continuation.resume(status.state as? String)
            },
            { _: dynamic ->
                if (continuation.isActive) continuation.resume(null)
            },
        )
        Unit
    }
